import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'

export type AnalyticsPeriod = 'day' | 'week' | 'month' | 'lifetime'

const PERIOD_KEY = 'mate-admin-analytics-period'
const periods: AnalyticsPeriod[] = ['day', 'week', 'month', 'lifetime']

type AnalyticsPeriodValue = {
  period: AnalyticsPeriod
  setPeriod: (period: AnalyticsPeriod) => void
}

const AnalyticsPeriodContext = createContext<AnalyticsPeriodValue | null>(null)

export function AnalyticsPeriodProvider({ children }: { children: ReactNode }) {
  const [period, setPeriodState] = useState<AnalyticsPeriod>(() => {
    const stored = localStorage.getItem(PERIOD_KEY)
    return periods.includes(stored as AnalyticsPeriod) ? stored as AnalyticsPeriod : 'lifetime'
  })

  const value = useMemo(() => ({
    period,
    setPeriod: (next: AnalyticsPeriod) => {
      localStorage.setItem(PERIOD_KEY, next)
      setPeriodState(next)
    },
  }), [period])

  return <AnalyticsPeriodContext.Provider value={value}>{children}</AnalyticsPeriodContext.Provider>
}

export function useAnalyticsPeriod() {
  const value = useContext(AnalyticsPeriodContext)
  if (!value) throw new Error('useAnalyticsPeriod must be used inside AnalyticsPeriodProvider')
  return value
}

export function AnalyticsPeriodToggle() {
  const { period, setPeriod } = useAnalyticsPeriod()
  return (
    <div className="join" aria-label="Analytics time range">
      {periods.map((option) => (
        <button
          key={option}
          type="button"
          className={`btn btn-xs sm:btn-sm join-item ${period === option ? 'btn-primary' : 'btn-ghost'}`}
          aria-pressed={period === option}
          onClick={() => setPeriod(option)}
        >
          {option[0].toUpperCase() + option.slice(1)}
        </button>
      ))}
    </div>
  )
}

export const periodToDays = (period: AnalyticsPeriod): number | undefined =>
  period === 'day' ? 1 : period === 'week' ? 7 : period === 'month' ? 30 : undefined

export const isWithinPeriod = (date: string, period: AnalyticsPeriod): boolean => {
  const days = periodToDays(period)
  return days === undefined || new Date(date).getTime() >= Date.now() - days * 86_400_000
}
